import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@services/api'
import { reminderMessage, waLink } from '../utils/whatsapp'
import { WhatsAppIcon } from '../components/WhatsAppIcon'

interface AgingItem { id: number; invoiceNo: string; store: string; storeId: number | null; invoiceDate: string; dueDate: string | null; ageDays: number; overdueDays: number; balance: number; type: string }
interface AgingBucket { bucket: string; amount: number; stores: number; invoices: number; items: AgingItem[] }
interface OverdueStore {
  id: number; name: string; phone: string | null; route: string | null; outstanding: number; overdue: number; overdueDays: number
  reliabilityGrade: 'A' | 'B' | 'C' | 'New'; onTimePct: number | null
  invoices: Array<{ id: number; invoiceNo: string; balance: number; overdueDays: number; dueDate: string | null }>
  autoQueue: string | null
  lastReminder: { at: string; channel: string; trigger: string; status: string } | null
}
interface Summary {
  asOf: string
  kpis: {
    todaysSales: { value: number; invoices: number; changeVsYesterdayPct: number | null; yesterday: number }
    cashCreditSplit: { cash: number; credit: number; cashAmount: number; creditAmount: number }
    outstandingCredit: { value: number; stores: number; invoices: number }
    overdue30: { value: number; stores: number; invoices: number }
    collectedToday: { value: number; receipts: number }
  }
  aging: AgingBucket[]
  overdueStores: OverdueStore[]
  collectionsToday: Array<{ id: number; receiptNo: string; invoiceNo: string; store: string; amount: number; mode: string; referenceNo: string | null; recordedBy: string; at: string }>
  routes: Array<{ route: string; sales: number; collected: number; outstanding: number; pct: number }>
  reminders: Array<{ id: number; storeId: number; amount: number; channel: string; trigger: string; status: string; createdAt: string; invoiceNos: string[] }>
}

const today = () => new Date().toISOString().split('T')[0]
const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const compact = (n: number) => (Math.abs(n) >= 100000 ? `₹${(n / 100000).toFixed(n >= 1000000 ? 1 : 2).replace(/\.?0+$/, '')}L` : formatINR(n))
const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
const ago = (iso: string) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  return mins < 1 ? 'just now' : mins < 60 ? `${mins} min ago` : mins < 1440 ? `${Math.round(mins / 60)}h ago` : `${Math.round(mins / 1440)}d ago`
}

export function SalesCollections() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ['collections-summary'], queryFn: () => apiClient.get<any>('/collections/summary'), refetchInterval: 60000 })
  const s: Summary | undefined = data?.data
  const { data: companyData } = useQuery({ queryKey: ['company-wa'], queryFn: () => apiClient.get<any>('/company') })
  const company = companyData?.data
  // Click-to-chat: opens WhatsApp with the reminder typed out; the biller presses Send. The reminder is also logged against the store.
  const whatsappRemind = (st: OverdueStore) => {
    const link = waLink(st.phone, reminderMessage({ store: st.name, overdue: st.overdue, outstanding: st.outstanding, invoices: st.invoices, company: company?.name || '', whatsapp: company?.whatsappNumber, upiId: company?.upiId }))
    if (!link) { setNotice({ kind: 'error', text: `${st.name} has no valid mobile number saved, so WhatsApp cannot be opened.` }); return }
    window.open(link, '_blank', 'noopener')
    apiClient.post<any>('/collections/reminders', { storeId: st.id }).then(() => refetch()).catch(() => undefined)
  }
  const [openBucket, setOpenBucket] = useState<string | null>(null)
  const [openStore, setOpenStore] = useState<number | null>(null)
  const [reminding, setReminding] = useState<'all' | number | null>(null)
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string; details?: string[] } | null>(null)
  const [exportRange, setExportRange] = useState({ from: today(), to: today() })
  const [showExport, setShowExport] = useState(false)
  const [exporting, setExporting] = useState(false)

  const sendReminders = async (storeId?: number) => {
    setNotice(null)
    setReminding(storeId ?? 'all')
    try {
      const res = await apiClient.post<any>('/collections/reminders', storeId ? { storeId } : {})
      setNotice({ kind: 'success', text: res.message, details: res.data.items.map((i: any) => `${i.store}${i.phone ? ` (${i.phone})` : ''} — ${formatINR(i.amount)}, ${i.overdueDays}d overdue → ${i.status}${i.status === 'Queued' ? ` via ${i.channel}` : ''}`) })
      await refetch()
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.response?.data?.message || 'Could not queue reminders' })
    } finally {
      setReminding(null)
    }
  }

  const exportTally = async () => {
    setNotice(null)
    if (exportRange.from > exportRange.to) return setNotice({ kind: 'error', text: 'From date must be on or before the To date' })
    setExporting(true)
    try {
      const res = await apiClient.get<Blob>(`/collections/export/tally?from=${exportRange.from}&to=${exportRange.to}`, { responseType: 'blob' })
      const blob = res instanceof Blob ? res : new Blob([res as any], { type: 'application/xml' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `tally-${exportRange.from}${exportRange.from !== exportRange.to ? `_to_${exportRange.to}` : ''}.xml`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
      const preview = await apiClient.get<any>(`/collections/export/summary?from=${exportRange.from}&to=${exportRange.to}`)
      setNotice({ kind: 'success', text: `Tally XML downloaded — ${preview.data.invoices} sales voucher${preview.data.invoices === 1 ? '' : 's'} and ${preview.data.receipts} receipt voucher${preview.data.receipts === 1 ? '' : 's'} for ${exportRange.from}${exportRange.from !== exportRange.to ? ` to ${exportRange.to}` : ''}. Import it in Tally via Gateway of Tally → Import Data → Vouchers.` })
      setShowExport(false)
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.response?.data?.message || 'Export failed' })
    } finally {
      setExporting(false)
    }
  }

  const k = s?.kpis
  const change = k?.todaysSales.changeVsYesterdayPct ?? null

  return (
    <div>
      <div className="hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Sales &amp; Collections</h1>
          <p>Where the business stands right now — collections, overdue credit, and today's performance.</p>
        </div>
        <button type="button" className="btn-ghost" onClick={() => setShowExport((v) => !v)}>Export to Tally</button>
      </div>

      {showExport && (
        <div className="card" style={{ background: 'var(--primary-soft)' }}>
          <h2>Export to Tally</h2>
          <div className="row3">
            <div><label>From</label><input type="date" value={exportRange.from} max={today()} onChange={(e) => setExportRange({ ...exportRange, from: e.target.value })} /></div>
            <div><label>To</label><input type="date" value={exportRange.to} max={today()} onChange={(e) => setExportRange({ ...exportRange, to: e.target.value })} /></div>
            <div className="flex items-end gap-2">
              <button type="button" className="btn-primary" onClick={exportTally} disabled={exporting}>{exporting ? 'Preparing…' : 'Download Tally XML'}</button>
              <button type="button" className="btn-ghost" onClick={() => setShowExport(false)}>Cancel</button>
            </div>
          </div>
          <div className="hint">Sales vouchers (party, Sales, Output CGST/SGST, stock items) for every invoice and Receipt vouchers (Cash / UPI Collections / Bank) for every collection in the range — the day's books, straight into Tally.</div>
        </div>
      )}

      {notice && (
        <div className={`alert ${notice.kind === 'success' ? 'alert-success' : 'alert-error'}`}>
          {notice.text}
          {notice.details && notice.details.length > 0 && (
            <ul className="mt-1 ml-4 list-disc text-[12px]">{notice.details.map((d, i) => <li key={i}>{d}</li>)}</ul>
          )}
        </div>
      )}

      <div className="kpis">
        <div className="kpi">
          <div className="label">Today's Sales</div>
          <div className="val">{k ? compact(k.todaysSales.value) : '—'}</div>
          <div className={`sub ${change === null ? '' : change >= 0 ? 'text-good' : 'text-danger'}`}>{k ? (change === null ? `${k.todaysSales.invoices} invoice${k.todaysSales.invoices === 1 ? '' : 's'} today` : `${change >= 0 ? '+' : ''}${change}% vs yesterday`) : ''}</div>
        </div>
        <div className="kpi">
          <div className="label">Cash / Credit Split</div>
          <div className="val">{k ? `${k.cashCreditSplit.cash} / ${k.cashCreditSplit.credit}` : '—'}</div>
          <div className="sub">% of this month's sales</div>
        </div>
        <div className="kpi">
          <div className="label">Outstanding Credit</div>
          <div className="val">{k ? compact(k.outstandingCredit.value) : '—'}</div>
          <div className={`sub ${k && k.outstandingCredit.value > 0 ? 'text-danger' : ''}`}>{k ? `Across ${k.outstandingCredit.stores} store${k.outstandingCredit.stores === 1 ? '' : 's'}` : ''}</div>
        </div>
        <div className="kpi">
          <div className="label">Overdue &gt;30 days</div>
          <div className="val">{k ? compact(k.overdue30.value) : '—'}</div>
          <div className={`sub ${k && k.overdue30.value > 0 ? 'text-danger' : 'text-good'}`}>{k ? (k.overdue30.stores ? `${k.overdue30.stores} store${k.overdue30.stores === 1 ? '' : 's'}` : 'Nothing past due') : ''}</div>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h2>Credit Aging</h2>
          <table>
            <thead><tr><th>Bucket</th><th className="num">Amount</th><th className="num">Stores</th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={3} className="text-ink-soft">Loading…</td></tr>}
              {(s?.aging || []).map((b) => (
                <Fragment key={b.bucket}>
                  <tr onClick={() => setOpenBucket(openBucket === b.bucket ? null : b.bucket)} style={{ cursor: b.invoices ? 'pointer' : 'default' }} title={b.invoices ? 'Click to see the invoices in this bucket' : ''}>
                    <td>{b.bucket}{b.invoices ? <span className="text-ink-soft text-[11px] ml-1">{openBucket === b.bucket ? '▾' : '▸'} {b.invoices} inv</span> : null}</td>
                    <td className="num">{compact(b.amount)}</td>
                    <td className="num">{b.stores}</td>
                  </tr>
                  {openBucket === b.bucket && b.items.length > 0 && (
                    <tr>
                      <td colSpan={3} className="!py-2" style={{ background: '#FAFAF7' }}>
                        {b.items.map((i) => (
                          <div key={i.id} className="flex justify-between gap-3 py-1 text-[12px] border-b border-[#EEEDE7] last:border-b-0">
                            <div><Link to={`/dashboard/sales/invoices/${i.id}`} className="font-semibold text-primary-500 hover:underline">{i.invoiceNo}</Link> · {i.store} · {formatDate(i.invoiceDate)}{i.overdueDays > 0 ? <span className="text-danger"> · {i.overdueDays}d overdue</span> : i.dueDate ? ` · due ${formatDate(i.dueDate)}` : ''}</div>
                            <div className="font-semibold">{formatINR(i.balance)}</div>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          <div className="hint">Open credit by invoice age. Reminders auto-queue at 15 and 30 days overdue; the 30+ bucket is what the dashboard flags.</div>
        </div>

        <div className="card">
          <h2>Top Overdue Stores</h2>
          <table>
            <thead><tr><th>Store</th><th className="num">Outstanding</th><th className="num">Days</th><th>Reliability</th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={4} className="text-ink-soft">Loading…</td></tr>}
              {!isLoading && (s?.overdueStores || []).length === 0 && <tr><td colSpan={4} className="text-ink-soft">No store is past its due date. 🎉</td></tr>}
              {(s?.overdueStores || []).map((st) => (
                <Fragment key={st.id}>
                  <tr onClick={() => setOpenStore(openStore === st.id ? null : st.id)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="font-medium">{st.name}</div>
                      <div className="text-[11px] text-ink-soft">
                        {st.route || 'No route'}{st.lastReminder ? ` · reminded ${ago(st.lastReminder.at)} (${st.lastReminder.channel})` : st.autoQueue ? ` · auto-queued at ${st.autoQueue.replace('auto-', '')}d` : ''}
                      </div>
                    </td>
                    <td className="num">{formatINR(st.overdue)}</td>
                    <td className="num">{st.overdueDays}</td>
                    <td><span className={`rel-badge ${st.reliabilityGrade === 'New' ? 'B' : st.reliabilityGrade}`}>{st.reliabilityGrade === 'New' ? 'New' : `Grade ${st.reliabilityGrade}`}</span></td>
                  </tr>
                  {openStore === st.id && (
                    <tr>
                      <td colSpan={4} className="!py-2" style={{ background: '#FAFAF7' }}>
                        {st.invoices.map((i) => (
                          <div key={i.id} className="flex justify-between gap-3 py-1 text-[12px] border-b border-[#EEEDE7] last:border-b-0">
                            <div><Link to={`/dashboard/sales/invoices/${i.id}`} className="font-semibold text-primary-500 hover:underline">{i.invoiceNo}</Link>{i.dueDate ? ` · due ${formatDate(i.dueDate)}` : ''} · <span className="text-danger">{i.overdueDays}d overdue</span></div>
                            <div className="flex gap-2 items-center"><span className="font-semibold">{formatINR(i.balance)}</span><Link to={`/dashboard/sales/invoices/${i.id}`} className="btn-ghost btn-xs">Record collection →</Link></div>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2 items-center text-[11.5px] text-ink-soft">
                          {st.phone ? `Ph ${st.phone}` : 'No phone on record'} · total outstanding {formatINR(st.outstanding)}{st.onTimePct !== null ? ` · ${st.onTimePct}% on-time` : ''}
                          <button type="button" className="btn-ghost btn-xs" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, borderColor: '#25D366' }} data-testid="wa-remind" onClick={(e) => { e.stopPropagation(); whatsappRemind(st) }} title="Opens WhatsApp with the reminder ready to send"><WhatsAppIcon size={14} />WhatsApp reminder</button>
                          <button type="button" className="btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); sendReminders(st.id) }} disabled={reminding !== null}>{reminding === st.id ? 'Queuing…' : 'Remind this store'}</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          <div className="btnrow">
            <button type="button" className="btn-ghost" onClick={() => sendReminders()} disabled={reminding !== null || !(s?.overdueStores || []).length}>{reminding === 'all' ? 'Queuing…' : 'Send Collection Reminders'}</button>
          </div>
          <div className="hint">WhatsApp/SMS reminders auto-queue at 15 and 30 days overdue — this button forces an immediate resend.</div>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <div className="field-label-row">
            <h2 style={{ marginBottom: 0 }}>Collections Today</h2>
            <span className="text-[12px] text-ink-soft">{k ? `${formatINR(k.collectedToday.value)} · ${k.collectedToday.receipts} receipt${k.collectedToday.receipts === 1 ? '' : 's'}` : ''}</span>
          </div>
          <table style={{ marginTop: 8 }}>
            <thead><tr><th>Receipt</th><th>Store</th><th>Against</th><th className="num">Amount</th><th>Mode</th></tr></thead>
            <tbody>
              {(s?.collectionsToday || []).length === 0 && <tr><td colSpan={5} className="text-ink-soft">No collections recorded yet today.</td></tr>}
              {(s?.collectionsToday || []).map((c) => (
                <tr key={c.id}>
                  <td className="font-medium">{c.receiptNo}</td>
                  <td>{c.store}</td>
                  <td>{c.invoiceNo}</td>
                  <td className="num">{formatINR(c.amount)}</td>
                  <td><span className={`badge ${c.mode === 'Cash' || c.mode === 'UPI' ? 'badge-cash' : 'badge-credit'}`}>{c.mode}</span>{c.referenceNo ? <span className="text-[11px] text-ink-soft ml-1">{c.referenceNo}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="hint">Cash/UPI sales post here automatically; credit invoices land here when a collection is recorded on the invoice.</div>
        </div>

        <div className="card">
          <h2>Route Collections — This Month</h2>
          <table>
            <thead><tr><th>Route</th><th className="num">Sales</th><th className="num">Collected</th><th className="num">Outstanding</th><th>Collection %</th></tr></thead>
            <tbody>
              {(s?.routes || []).length === 0 && <tr><td colSpan={5} className="text-ink-soft">No sales this month yet.</td></tr>}
              {(s?.routes || []).map((r) => (
                <tr key={r.route}>
                  <td>{r.route}</td>
                  <td className="num">{compact(r.sales)}</td>
                  <td className="num">{compact(r.collected)}</td>
                  <td className={`num ${r.outstanding > 0 ? 'text-danger' : ''}`}>{compact(r.outstanding)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="h-[6px] rounded bg-[#EEEDE7] flex-1 min-w-[60px]"><div className="h-[6px] rounded" style={{ width: `${Math.min(100, r.pct)}%`, background: r.pct >= 90 ? 'var(--good)' : r.pct >= 75 ? 'var(--accent)' : 'var(--danger)' }} /></div>
                      <span className="text-[12px] font-semibold w-10 text-right">{r.pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="hint">Collected ÷ sales for invoices dated this month, by the store's route; walk-in sales sit under the cash &amp; carry counter.</div>
        </div>
      </div>
    </div>
  )
}
